import { get, writable } from "svelte/store";
import lineArc from "@turf/line-arc";
import { point } from "@turf/helpers";
import Mexp from "math-expression-evaluator/dist/browser/math-expression-evaluator.min.js";

import GeoHelper from "../../../Helpers/GeoHelper";
import roundFloat from "../../../Helpers/Misc/roundFloat";
import DistanceUtil from "../DistanceUtil";
import DistanceUnit from "../DistanceUnit";

const ITEM_COLORS = ["#1baa36", "#ff7c1b", "red", "#156fff", "black"];

class RangeRing {
	constructor(index, color = null) {
		if (color === null) {
			color = ITEM_COLORS[index % ITEM_COLORS.length];
		}
		this.meta = {
			index: index,
			color: writable(color)
		};
		this.input = {
			lng: writable(0),
			lat: writable(0),
			time: writable(""),
			speed: writable("")
		};
		this.result = {
			arc: writable(null),
			label: writable(null),
			distance: {
				nauticalMiles: writable(0),
				km: writable(0)
			}
		};
		this.error = writable("");
	}

	setPosition(lng, lat) {
		this.input.lng.set(lng);
		this.input.lat.set(lat);
	}

	recalculate() {
		const item = this;
		let inputTime = "" + get(item.input.time);
		let inputSpeed = "" + get(item.input.speed);

		try {
			item.error.set("");
			item.result.label.set("");
			item.result.distance.nauticalMiles.set("");
			item.result.distance.km.set("");
			item.result.arc.set(null);

			if (inputTime.length > 0 && inputSpeed.length > 0) {
				inputTime = Mexp.eval(inputTime);
				inputSpeed = Mexp.eval(inputSpeed);

				if (Number.isNaN(parseFloat(inputTime))) {
					item.error.set("Time is invalid (nan)");
				}
				else if (Number.isNaN(parseFloat(inputSpeed))) {
					item.error.set("Speed is invalid (nan)");
				}
				else {
					const lng = get(item.input.lng);
					const lat = get(item.input.lat);
					const center = point([lng, lat]);
					const distanceNM = (inputSpeed * (inputTime / 60.0));
					const radiusMiles = DistanceUtil.convert(distanceNM, DistanceUnit.NAUTICAL_MILES, DistanceUnit.MILES);
					const radiusKM = DistanceUtil.convert(radiusMiles, DistanceUnit.MILES, DistanceUnit.KILOMETERS);

					const arc = lineArc(center, radiusMiles, 0, 360, { steps: 90, units: "miles" });
					arc.properties.color = get(item.meta.color);

					const lngEdge = arc.geometry.coordinates[0][0];
					const latEdge = arc.geometry.coordinates[0][1];

					const labelStr = roundFloat(distanceNM, 2) + " nautical miles\n(" + roundFloat(inputTime, 2) + " mins @ " + roundFloat(inputSpeed, 2) + " knots)";
					const label = GeoHelper.featureAt(lngEdge, latEdge, { description: labelStr, color: arc.properties.color });
					item.result.label.set(label);

					item.result.distance.nauticalMiles.set("" + distanceNM);
					item.result.distance.km.set("" + radiusKM);
					item.result.arc.set(arc);
				}
			}
		}
		catch (e) {
			console.trace(e);
			item.error.set(e.message);
		}
	}

	setRadius(radiusKm, labelPrefix = "") {
		const item = this;
		const lng = get(item.input.lng);
		const lat = get(item.input.lat);
		const center = point([lng, lat]);
		const arc = lineArc(center, radiusKm, 0, 360, { steps: 90, units: "kilometers" });
		arc.properties.color = get(item.meta.color);

		const labelStr = labelPrefix + roundFloat(radiusKm, 2) + " kilometers";
		const label = GeoHelper.featureAt(arc.geometry.coordinates[0][0], arc.geometry.coordinates[0][1], {
			description: labelStr,
			color: arc.properties.color
		});

		item.result.arc.set(arc);
		item.result.distance.km.set("" + radiusKm);
		item.result.label.set(label);
	}
}
export default RangeRing;
